import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { hasMinRole } from '../../utils/roles';

const emptyAccount = {
  label: '',
  email_address: '',
  smtp_host: 'smtp.office365.com',
  smtp_port: 587,
  smtp_secure: false,
  password: '',
};

const csv = (value) => (Array.isArray(value) ? value.join(', ') : '');
const splitEmails = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const SystemEmailSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [functionDrafts, setFunctionDrafts] = useState({});
  const [accountForm, setAccountForm] = useState(emptyAccount);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [testAccount, setTestAccount] = useState(null);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const accounts = config?.accounts || [];
  const functions = config?.functions || [];
  const canAddAccount = accounts.length < (config?.max_accounts || 5) && !editingAccountId;

  const accountOptions = useMemo(() => accounts.map((account) => ({
    id: account.id,
    label: `${account.label} (${account.email_address})`,
  })), [accounts]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await makeApiCall('/api/admin/system-email/config');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('systemEmail.errors.load'));
      setConfig(data);
      const drafts = {};
      (data.functions || []).forEach((item) => {
        drafts[item.function_key] = {
          from_account_id: item.from_account_id || '',
          default_to: csv(item.default_to),
          default_cc: csv(item.default_cc),
          default_bcc: csv(item.default_bcc),
          is_enabled: item.is_enabled !== false,
        };
      });
      setFunctionDrafts(drafts);
    } catch (err) {
      setError(err.message || t('systemEmail.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetAccountForm = () => {
    setEditingAccountId(null);
    setAccountForm(emptyAccount);
  };

  const editAccount = (account) => {
    setEditingAccountId(account.id);
    setAccountForm({
      label: account.label || '',
      email_address: account.email_address || '',
      smtp_host: account.smtp_host || 'smtp.office365.com',
      smtp_port: account.smtp_port || 587,
      smtp_secure: !!account.smtp_secure,
      password: '',
    });
  };

  const saveAccount = async () => {
    try {
      setSaving(true);
      setError('');
      const endpoint = editingAccountId
        ? `/api/admin/system-email/accounts/${editingAccountId}`
        : '/api/admin/system-email/accounts';
      const method = editingAccountId ? 'PUT' : 'POST';
      const response = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(accountForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('systemEmail.errors.saveAccount'));
      setSuccess(t('systemEmail.messages.accountSaved'));
      resetAccountForm();
      await loadConfig();
    } catch (err) {
      setError(err.message || t('systemEmail.errors.saveAccount'));
    } finally {
      setSaving(false);
    }
  };

  const deactivateAccount = async (account) => {
    try {
      setSaving(true);
      setError('');
      const response = await makeApiCall(`/api/admin/system-email/accounts/${account.id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('systemEmail.errors.deleteAccount'));
      setSuccess(t('systemEmail.messages.accountRemoved'));
      await loadConfig();
    } catch (err) {
      setError(err.message || t('systemEmail.errors.deleteAccount'));
    } finally {
      setSaving(false);
    }
  };

  const updateFunctionDraft = (key, field, value) => {
    setFunctionDrafts((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        [field]: value,
      },
    }));
  };

  const saveFunction = async (item) => {
    try {
      setSaving(true);
      setError('');
      const draft = functionDrafts[item.function_key] || {};
      const response = await makeApiCall(`/api/admin/system-email/functions/${item.function_key}`, {
        method: 'PUT',
        body: JSON.stringify({
          from_account_id: draft.from_account_id || null,
          default_to: splitEmails(draft.default_to),
          default_cc: splitEmails(draft.default_cc),
          default_bcc: splitEmails(draft.default_bcc),
          is_enabled: draft.is_enabled !== false,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('systemEmail.errors.saveFunction'));
      setSuccess(t('systemEmail.messages.functionSaved'));
      await loadConfig();
    } catch (err) {
      setError(err.message || t('systemEmail.errors.saveFunction'));
    } finally {
      setSaving(false);
    }
  };

  const saveAllFunctions = async () => {
    try {
      setSaving(true);
      setError('');
      for (const item of functions) {
        const draft = functionDrafts[item.function_key] || {};
        const response = await makeApiCall(`/api/admin/system-email/functions/${item.function_key}`, {
          method: 'PUT',
          body: JSON.stringify({
            from_account_id: draft.from_account_id || null,
            default_to: splitEmails(draft.default_to),
            default_cc: splitEmails(draft.default_cc),
            default_bcc: splitEmails(draft.default_bcc),
            is_enabled: draft.is_enabled !== false,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || t('systemEmail.errors.saveFunction'));
      }
      setSuccess(t('systemEmail.messages.allFunctionsSaved'));
      await loadConfig();
    } catch (err) {
      setError(err.message || t('systemEmail.errors.saveFunction'));
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = (account) => {
    setTestAccount(account);
    setTestEmail(user?.email || '');
  };

  const sendTest = async () => {
    if (!testAccount) return;
    try {
      setTesting(true);
      setError('');
      const response = await makeApiCall(`/api/admin/system-email/accounts/${testAccount.id}/test`, {
        method: 'POST',
        body: JSON.stringify({ to_email: testEmail }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('systemEmail.errors.test'));
      setSuccess(t('systemEmail.messages.testSent'));
      setTestAccount(null);
      setTestEmail('');
    } catch (err) {
      setError(err.message || t('systemEmail.errors.test'));
    } finally {
      setTesting(false);
    }
  };

  if (!hasMinRole(user, 'admin')) {
    return (
      <Alert severity="error">
        {t('systemEmail.adminOnly')}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000062' }}>
            {t('systemEmail.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('systemEmail.description')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadConfig}
          aria-label={t('systemEmail.refreshAria')}
        >
          {t('common.refresh')}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {!config?.encryption_configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('systemEmail.encryptionMissing')}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          {t('systemEmail.accountsTitle')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            label={t('systemEmail.accountLabel')}
            value={accountForm.label}
            onChange={(event) => setAccountForm((current) => ({ ...current, label: event.target.value }))}
            disabled={!canAddAccount && !editingAccountId}
            inputProps={{ 'aria-label': t('systemEmail.accountLabel') }}
          />
          <TextField
            label={t('systemEmail.emailAddress')}
            type="email"
            value={accountForm.email_address}
            onChange={(event) => setAccountForm((current) => ({ ...current, email_address: event.target.value }))}
            disabled={!canAddAccount && !editingAccountId}
            inputProps={{ 'aria-label': t('systemEmail.emailAddress') }}
          />
          <TextField
            label={t('systemEmail.smtpHost')}
            value={accountForm.smtp_host}
            onChange={(event) => setAccountForm((current) => ({ ...current, smtp_host: event.target.value }))}
            disabled={!canAddAccount && !editingAccountId}
            inputProps={{ 'aria-label': t('systemEmail.smtpHost') }}
          />
          <TextField
            label={t('systemEmail.smtpPort')}
            type="number"
            value={accountForm.smtp_port}
            onChange={(event) => setAccountForm((current) => ({ ...current, smtp_port: event.target.value }))}
            disabled={!canAddAccount && !editingAccountId}
            inputProps={{ 'aria-label': t('systemEmail.smtpPort') }}
          />
          <TextField
            label={editingAccountId ? t('systemEmail.replacePassword') : t('systemEmail.smtpPassword')}
            type="password"
            value={accountForm.password}
            onChange={(event) => setAccountForm((current) => ({ ...current, password: event.target.value }))}
            helperText={editingAccountId ? t('systemEmail.passwordReplaceHelp') : t('systemEmail.passwordWriteOnlyHelp')}
            disabled={!canAddAccount && !editingAccountId}
            inputProps={{ 'aria-label': editingAccountId ? t('systemEmail.replacePassword') : t('systemEmail.smtpPassword') }}
          />
          <FormControlLabel
            control={(
              <Switch
                checked={!!accountForm.smtp_secure}
                onChange={(event) => setAccountForm((current) => ({ ...current, smtp_secure: event.target.checked }))}
                inputProps={{ 'aria-label': t('systemEmail.smtpSecure') }}
              />
            )}
            label={t('systemEmail.smtpSecure')}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveAccount}
            disabled={saving || (!canAddAccount && !editingAccountId)}
            aria-label={editingAccountId ? t('systemEmail.updateAccount') : t('systemEmail.addAccount')}
          >
            {editingAccountId ? t('systemEmail.updateAccount') : t('systemEmail.addAccount')}
          </Button>
          {editingAccountId && (
            <Button onClick={resetAccountForm} disabled={saving}>
              {t('common.cancel')}
            </Button>
          )}
          <Chip
            label={t('systemEmail.accountLimit', { count: accounts.length, max: config?.max_accounts || 5 })}
            variant="outlined"
          />
        </Stack>

        <TableContainer sx={{ mt: 3 }}>
          <Table size="small" aria-label={t('systemEmail.accountsTableAria')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('systemEmail.accountLabel')}</TableCell>
                <TableCell>{t('systemEmail.emailAddress')}</TableCell>
                <TableCell>{t('systemEmail.smtpServer')}</TableCell>
                <TableCell>{t('systemEmail.passwordStatus')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>{account.label}</TableCell>
                  <TableCell>{account.email_address}</TableCell>
                  <TableCell>{account.smtp_host}:{account.smtp_port}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={account.has_password ? 'success' : 'warning'}
                      label={account.has_password ? t('systemEmail.passwordSaved') : t('systemEmail.passwordMissing')}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('systemEmail.testAccount')}>
                      <IconButton onClick={() => openTestDialog(account)} aria-label={t('systemEmail.testAccount')}>
                        <SendIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.edit')}>
                      <IconButton onClick={() => editAccount(account)} aria-label={t('common.edit')}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton onClick={() => setDeleteAccountTarget(account)} aria-label={t('common.delete')}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {accounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Alert severity="info">{t('systemEmail.noAccounts')}</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('systemEmail.functionsTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('systemEmail.functionsHelp')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveAllFunctions}
            disabled={saving}
            aria-label={t('systemEmail.saveAllFunctions')}
          >
            {t('systemEmail.saveAllFunctions')}
          </Button>
        </Stack>
        <Stack spacing={2}>
          {functions.map((item) => {
            const draft = functionDrafts[item.function_key] || {};
            return (
              <Paper key={item.function_key} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                    {item.env_fallback_from && (
                      <Typography variant="caption" color="text.secondary">
                        {t('systemEmail.envFallback', { email: item.env_fallback_from })}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>{t('systemEmail.fromAccount')}</InputLabel>
                      <Select
                        label={t('systemEmail.fromAccount')}
                        value={draft.from_account_id || ''}
                        onChange={(event) => updateFunctionDraft(item.function_key, 'from_account_id', event.target.value)}
                        inputProps={{ 'aria-label': t('systemEmail.fromAccount') }}
                      >
                        <MenuItem value="">{t('systemEmail.useEnvironmentFallback')}</MenuItem>
                        {accountOptions.map((option) => (
                          <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={draft.is_enabled !== false}
                          onChange={(event) => updateFunctionDraft(item.function_key, 'is_enabled', event.target.checked)}
                          inputProps={{ 'aria-label': t('systemEmail.enabled') }}
                        />
                      )}
                      label={t('systemEmail.enabled')}
                    />
                    <TextField
                      label={t('systemEmail.defaultTo')}
                      value={draft.default_to || ''}
                      onChange={(event) => updateFunctionDraft(item.function_key, 'default_to', event.target.value)}
                      helperText={item.env_default_to?.length ? t('systemEmail.envDefaultTo', { emails: item.env_default_to.join(', ') }) : t('systemEmail.commaSeparated')}
                      inputProps={{ 'aria-label': t('systemEmail.defaultTo') }}
                    />
                    <TextField
                      label={t('systemEmail.defaultCc')}
                      value={draft.default_cc || ''}
                      onChange={(event) => updateFunctionDraft(item.function_key, 'default_cc', event.target.value)}
                      helperText={t('systemEmail.commaSeparated')}
                      inputProps={{ 'aria-label': t('systemEmail.defaultCc') }}
                    />
                    <TextField
                      label={t('systemEmail.defaultBcc')}
                      value={draft.default_bcc || ''}
                      onChange={(event) => updateFunctionDraft(item.function_key, 'default_bcc', event.target.value)}
                      helperText={item.env_default_bcc?.length ? t('systemEmail.envDefaultBcc', { emails: item.env_default_bcc.join(', ') }) : t('systemEmail.commaSeparated')}
                      inputProps={{ 'aria-label': t('systemEmail.defaultBcc') }}
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<SaveIcon />}
                      onClick={() => saveFunction(item)}
                      disabled={saving}
                      aria-label={t('systemEmail.saveFunction', { label: item.label })}
                    >
                      {t('systemEmail.saveFunctionButton')}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Paper>

      <Dialog open={!!testAccount} onClose={() => setTestAccount(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('systemEmail.testDialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {testAccount ? t('systemEmail.testDialogHelp', { email: testAccount.email_address }) : ''}
            </Typography>
            <TextField
              label={t('systemEmail.testRecipient')}
              type="email"
              value={testEmail}
              onChange={(event) => setTestEmail(event.target.value)}
              inputProps={{ 'aria-label': t('systemEmail.testRecipient') }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestAccount(null)} disabled={testing}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={sendTest}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
            aria-label={t('systemEmail.sendTest')}
          >
            {t('systemEmail.sendTest')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteAccountTarget}
        onClose={() => setDeleteAccountTarget(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="system-email-delete-dialog-title"
      >
        <DialogTitle id="system-email-delete-dialog-title">
          {t('systemEmail.deleteDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteAccountTarget
              ? t('systemEmail.deleteDialogHelp', { email: deleteAccountTarget.email_address })
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccountTarget(null)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={async () => {
              const target = deleteAccountTarget;
              setDeleteAccountTarget(null);
              if (target) await deactivateAccount(target);
            }}
            aria-label={t('systemEmail.deleteAccount')}
          >
            {t('systemEmail.deleteAccount')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemEmailSettings;
